<template>
  <div id="reportPage">
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-box">
        <div class="loading-spinner"></div>
        <div class="loading-text">Loading...</div>
      </div>
    </div>
    <main>
      <article>
        <div class="title-wrapper mb-4">
          <h2 class="login-title">DNQ REPORT</h2>
          <UserMenu :roles="roles" :user-name="userName" />
        </div>

        <!-- TOGGLE -->
          <div class="view-toggle mb-3">
            <span :class="['toggle-label', !showCharts ? 'active' : '']">Table</span>
            <div class="toggle-switch" @click="showCharts = !showCharts">
              <div :class="['toggle-knob', showCharts ? 'right' : 'left']"></div>
            </div>
            <span :class="['toggle-label', showCharts ? 'active' : '']">Charts</span>
          </div>

          <div v-if="showCharts" class="row mt-4">

          <div class="col-md-6 mb-3">
            <div class="card shadow-sm h-100">
              <div class="card-body text-center">
                <h5 class="card-title text-primary mb-3">
                  CALLS BY STATUS
                </h5>

                <apexchart
                  type="pie"
                  :options="pieChartOptions"
                  :series="pieChartSeries"
                  height="300"
                />
              </div>
            </div>
          </div>

          <div class="col-md-6 mb-3">
            <div class="card shadow-sm h-100">
              <div class="card-body text-center">
                <h5 class="card-title text-primary mb-3">
                  CALLS BY STATES
                </h5>

                <apexchart
                  type="pie"
                  :options="statePieOptions"
                  :series="statePieSeries"
                  height="300"
                />
              </div>
            </div>
          </div>

        </div>

          <div v-else class="row mt-4">
            <div class="col-12 mb-3">
              <div class="card shadow-sm h-100">
                <div class="card-body">
                  <div class="statuses-card-header mb-3">
                    <h5 class="card-title text-primary mb-0 text-center">
                      STATUSES BY STATE
                    </h5>
                    <a
                      href="#"
                      class="link-csv statuses-card-csv"
                      :class="{ 'link-csv--disabled': isExportingCsv }"
                      @click.prevent="downloadSummaryCSV"
                    >
                      <font-awesome-icon icon="file-csv" />
                      {{ isExportingCsv ? 'Preparing CSV…' : 'Download CSV' }}
                    </a>
                  </div>

                  <div class="table-responsive" style="max-height: 400px; overflow: auto;">
                    <table
                      class="table table-bordered table-sm text-center mb-0"
                      style="table-layout: fixed; width: 100%;"
                    >
                      <thead class="table-light" style="position: sticky; top: 0; z-index: 1;">
                        <tr>
                          <th>STATE</th>
                          <th v-for="status in allStatuses" :key="status">
                            {{ status }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(statuses, state) in stateStatusTable" :key="state">
                          <td>{{ state }}</td>
                          <td v-for="status in allStatuses" :key="status">
                            {{ statuses[status] }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
          <div class="dnq-main-table-section">
          <div class="card shadow-sm">
          <div class="card-body">
          <div class="table-top-filters">
            <DateRangePicker v-model="selectedRange" @change="onDateRangeChange" />
            <AutoRefresh v-model="refreshRate" @refresh="fetchData" />
            <a
              href="#"
              class="link-csv"
              :class="{ 'link-csv--disabled': isExportingCsv }"
              @click.prevent="downloadCSV"
            >
              <font-awesome-icon icon="file-csv" />
              {{ isExportingCsv ? 'Preparing CSV…' : 'Download CSV' }}
            </a>
          </div>
          


        <div class="table-responsive">
          <table class="table table-bordered align-middle dnq-data-table">
            <thead class="table-light">
              <tr>
                <th>DATE</th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>CAMPAIGN</span>
                    <button
                      type="button"
                      ref="campaignFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: campaignFilterSelectedCount > 0 }"
                      @click.stop="toggleCampaignFilterPopover"
                      title="Filter by campaign"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="campaignFilterSelectedCount > 0" class="filter-badge">{{ campaignFilterSelectedCount }}</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>PHONE</span>
                    <button
                      type="button"
                      ref="phoneFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: phoneFilterActive }"
                      @click.stop="togglePhoneFilterPopover"
                      title="Filter by phone number"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="phoneFilterActive" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>STATE</span>
                    <button
                      type="button"
                      ref="stateFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: stateFilterSelectedCount > 0 }"
                      @click.stop="toggleStateFilterPopover"
                      title="Filter by state"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="stateFilterSelectedCount > 0" class="filter-badge">{{ stateFilterSelectedCount }}</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>STATUS</span>
                    <button
                      type="button"
                      ref="statusFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: statusFilterSelectedCount > 0 }"
                      @click.stop="toggleStatusFilterPopover"
                      title="Filter by status"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="statusFilterSelectedCount > 0" class="filter-badge">{{ statusFilterSelectedCount }}</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>STATUS NAME</span>
                    <button
                      type="button"
                      ref="statusNameFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: statusNameFilterSelectedCount > 0 }"
                      @click.stop="toggleStatusNameFilterPopover"
                      title="Filter by status name"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="statusNameFilterSelectedCount > 0" class="filter-badge">{{ statusNameFilterSelectedCount }}</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>CALL DURATION</span>
                    <button
                      type="button"
                      ref="durationFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: durationFilterActive }"
                      @click.stop="toggleDurationFilterPopover"
                      title="Filter by call duration"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="durationFilterActive" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>FIRST NAME</span>
                    <button
                      type="button"
                      ref="firstNameFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: first_nameFilterActive }"
                      @click.stop="toggleFirstNameFilterPopover"
                      title="Filter by first name"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="first_nameFilterActive" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>LAST NAME</span>
                    <button
                      type="button"
                      ref="lastNameFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: last_nameFilterActive }"
                      @click.stop="toggleLastNameFilterPopover"
                      title="Filter by last name"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="last_nameFilterActive" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th>EMAIL</th>
                <th>OWN OR RENT</th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>INSURANCE COMPANY</span>
                    <button
                      type="button"
                      ref="insuranceFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: insuranceFilterSelectedCount > 0 }"
                      @click.stop="toggleInsuranceFilterPopover"
                      title="Filter by insurance company"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="insuranceFilterSelectedCount > 0" class="filter-badge">{{ insuranceFilterSelectedCount }}</span>
                    </button>
                  </div>
                </th>
                <th>DATE OF BIRTH</th>
                <th>VEHICLES</th>
                <th>ADDRESS</th>
                <th>CITY</th>
                <th>ZIP</th>
                <th title="Transfer Number">XFER #</th>
                <th>MAKE</th>
                <th>MODEL</th>
                <th>YEAR</th>
                <th title="Insured Since">INS. SINCE</th>
                <th title="Insurance Expiration">INS. EXPIRATION</th>
                <th>VIN</th>
                <th title="12 Months Continuously Insured">12 MO CONT.</th>
                <th>RECORDING</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in rows" :key="i">
                <td>{{ row.call_time }}</td>
                <td>{{ row.campaign_name }}</td>
                <td>{{ row.phone_number }}</td>
                <td>{{ row.state }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.status_name }}</td>
                <td>{{ row.length_in_sec }}</td>
                <td>{{ row.first_name }}</td>
                <td>{{ row.last_name }}</td>
                <td>{{ row.email }}</td>
                <td>{{ row.custom1 }}</td>
                <td>{{ row.custom2 }}</td>
                <td>{{ row.date_of_birth !== '0000-00-00' ? row.date_of_birth : '' }}</td>
                <td>{{ row.custom3 }}</td>
                <td>{{ row.address }}</td>
                <td>{{ row.city }}</td>
                <td>{{ row.zip }}</td>
                <td>{{ row.xfer_number }}</td>
                <td>{{ row.make }}</td>
                <td>{{ row.model }}</td>
                <td>{{ row.year }}</td>
                <td>{{ formatDnqDate(row.insured_since) }}</td>
                <td>{{ formatDnqDate(row.insurance_expiration) }}</td>
                <td>{{ row.vin }}</td>
                <td>{{ row.twelve_months_continuously_insured }}</td>
                <td class="text-center recording-cell">
                  <div class="recording-cell-actions">
                  <template v-if="row.recording_file">
                    <a @click="downloadRecording(row)" class="audio-btn download-btn ms-2">
                      <font-awesome-icon icon="download" />
                    </a>
                    <button class="audio-btn play-btn ms-2" @click="toggleAudio(i, row)">
                      <font-awesome-icon v-if="!audioPlaying[i]" icon="play" />
                      <font-awesome-icon v-else icon="pause" />
                    </button>
                    <audio 
                      :id="`audio-${row.id || i}`"
                      :src="'/audio/' + row.recording_file" 
                      preload="none" 
                      @ended="audioEnded(i)" 
                      @error="audioError(i)"
                      style="display: none;"
                    >
                    </audio>
                  </template>
                  <template v-else>
                    <span>Pending</span>
                  </template>
                  </div>
                </td>
              </tr>
              <tr v-if="rows.length === 0">
                <td colspan="26" class="text-center py-3">No results</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="d-flex justify-content-center align-items-center gap-2 mt-3">
          <button
            class="btn btn-sm btn-outline-primary"
            :disabled="currentPage === 1"
            @click="changePage(currentPage - 1)"
          >
            ◀ Prev
          </button>

          <span>
            Page {{ currentPage }} of {{ totalPages }}
          </span>

          <button
            class="btn btn-sm btn-outline-primary"
            :disabled="currentPage >= totalPages"
            @click="changePage(currentPage + 1)"
          >
            Next ▶
          </button>
        </div>

        <button class="btn-submit mt-3" @click="clearFilters">Clear filters</button>
          </div>
          </div>
          </div>
            </div>
          </div>

      <!-- Campaign filter popover (opens near icon) -->
      <Teleport to="body">
        <div
          v-if="showCampaignFilterModal"
          ref="campaignFilterPopover"
          class="filter-popover"
          :style="campaignFilterPopoverStyle"
        >
        <div class="filter-popover-header">
          <span class="filter-popover-title">Filter by Campaign</span>
          <button type="button" class="filter-popover-close" @click="closeCampaignFilterModal" aria-label="Close">×</button>
        </div>
        <div class="filter-popover-body">
          <input
            v-model="campaignFilterSearch"
            type="text"
            class="filter-popover-search"
            placeholder="Search (contains)..."
          />
          <div class="filter-popover-list">
            <label
              v-for="opt in filteredCampaignOptions"
              :key="opt"
              class="filter-popover-item"
            >
              <input
                type="checkbox"
                :value="opt"
                v-model="campaignFilterSelected"
              />
              <span>{{ opt }}</span>
            </label>
            <div v-if="filteredCampaignOptions.length === 0" class="filter-popover-empty">
              No campaigns match
            </div>
          </div>
        </div>
        <div class="filter-popover-footer">
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="selectAllCampaigns">All</button>
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="deselectAllCampaigns">None</button>
          <button type="button" class="btn btn-sm btn-primary" @click="applyCampaignFilter">Apply</button>
        </div>
        </div>
        <!-- State filter popover -->
        <div
          v-if="showStateFilterModal"
          ref="stateFilterPopover"
          class="filter-popover"
          :style="stateFilterPopoverStyle"
        >
        <div class="filter-popover-header">
          <span class="filter-popover-title">Filter by State</span>
          <button type="button" class="filter-popover-close" @click="closeStateFilterModal" aria-label="Close">×</button>
        </div>
        <div class="filter-popover-body">
          <input
            v-model="stateFilterSearch"
            type="text"
            class="filter-popover-search"
            placeholder="Search (contains)..."
          />
          <div class="filter-popover-list">
            <label
              v-for="opt in filteredStateOptions"
              :key="opt"
              class="filter-popover-item"
            >
              <input
                type="checkbox"
                :value="opt"
                v-model="stateFilterSelected"
              />
              <span>{{ opt }}</span>
            </label>
            <div v-if="filteredStateOptions.length === 0" class="filter-popover-empty">
              No states match
            </div>
          </div>
        </div>
        <div class="filter-popover-footer">
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="selectAllStates">All</button>
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="deselectAllStates">None</button>
          <button type="button" class="btn btn-sm btn-primary" @click="applyStateFilter">Apply</button>
        </div>
        </div>
        <!-- Status filter popover -->
        <div
          v-if="showStatusFilterModal"
          ref="statusFilterPopover"
          class="filter-popover"
          :style="statusFilterPopoverStyle"
        >
        <div class="filter-popover-header">
          <span class="filter-popover-title">Filter by Status</span>
          <button type="button" class="filter-popover-close" @click="closeStatusFilterModal" aria-label="Close">×</button>
        </div>
        <div class="filter-popover-body">
          <input
            v-model="statusFilterSearch"
            type="text"
            class="filter-popover-search"
            placeholder="Search (contains)..."
          />
          <div class="filter-popover-list">
            <label
              v-for="opt in filteredStatusOptions"
              :key="opt"
              class="filter-popover-item"
            >
              <input
                type="checkbox"
                :value="opt"
                v-model="statusFilterSelected"
              />
              <span>{{ opt }}</span>
            </label>
            <div v-if="filteredStatusOptions.length === 0" class="filter-popover-empty">
              No statuses match
            </div>
          </div>
        </div>
        <div class="filter-popover-footer">
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="selectAllStatuses">All</button>
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="deselectAllStatuses">None</button>
          <button type="button" class="btn btn-sm btn-primary" @click="applyStatusFilter">Apply</button>
        </div>
        </div>
        <!-- Status Name filter popover -->
        <div
          v-if="showStatusNameFilterModal"
          ref="statusNameFilterPopover"
          class="filter-popover"
          :style="statusNameFilterPopoverStyle"
        >
        <div class="filter-popover-header">
          <span class="filter-popover-title">Filter by Status Name</span>
          <button type="button" class="filter-popover-close" @click="closeStatusNameFilterModal" aria-label="Close">×</button>
        </div>
        <div class="filter-popover-body">
          <input
            v-model="statusNameFilterSearch"
            type="text"
            class="filter-popover-search"
            placeholder="Search (contains)..."
          />
          <div class="filter-popover-list">
            <label
              v-for="opt in filteredStatusNameOptions"
              :key="opt"
              class="filter-popover-item"
            >
              <input
                type="checkbox"
                :value="opt"
                v-model="statusNameFilterSelected"
              />
              <span>{{ opt }}</span>
            </label>
            <div v-if="filteredStatusNameOptions.length === 0" class="filter-popover-empty">
              No status names match
            </div>
          </div>
        </div>
        <div class="filter-popover-footer">
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="selectAllStatusNames">All</button>
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="deselectAllStatusNames">None</button>
          <button type="button" class="btn btn-sm btn-primary" @click="applyStatusNameFilter">Apply</button>
        </div>
        </div>
        <!-- Insurance filter popover -->
        <div
          v-if="showInsuranceFilterModal"
          ref="insuranceFilterPopover"
          class="filter-popover"
          :style="insuranceFilterPopoverStyle"
        >
        <div class="filter-popover-header">
          <span class="filter-popover-title">Filter by Insurance Company</span>
          <button type="button" class="filter-popover-close" @click="closeInsuranceFilterModal" aria-label="Close">×</button>
        </div>
        <div class="filter-popover-body">
          <input
            v-model="insuranceFilterSearch"
            type="text"
            class="filter-popover-search"
            placeholder="Search (contains)..."
          />
          <div class="filter-popover-list">
            <label
              v-for="opt in filteredInsuranceOptions"
              :key="opt"
              class="filter-popover-item"
            >
              <input
                type="checkbox"
                :value="opt"
                v-model="insuranceFilterSelected"
              />
              <span>{{ opt }}</span>
            </label>
            <div v-if="filteredInsuranceOptions.length === 0" class="filter-popover-empty">
              No insurance companies match
            </div>
          </div>
        </div>
        <div class="filter-popover-footer">
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="selectAllInsurance">All</button>
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="deselectAllInsurance">None</button>
          <button type="button" class="btn btn-sm btn-primary" @click="applyInsuranceFilter">Apply</button>
        </div>
        </div>
        <!-- Call duration filter popover -->
        <div
          v-if="showDurationFilterModal"
          ref="durationFilterPopover"
          class="filter-popover"
          :style="durationFilterPopoverStyle"
        >
        <div class="filter-popover-header">
          <span class="filter-popover-title">Filter by Call Duration (sec)</span>
          <button type="button" class="filter-popover-close" @click="closeDurationFilterModal" aria-label="Close">×</button>
        </div>
        <div class="filter-popover-body">
          <div class="duration-filter-mode">
            <label class="filter-popover-item">
              <input type="radio" value="exact" v-model="durationFilterMode" />
              <span>Exact value</span>
            </label>
            <label class="filter-popover-item">
              <input type="radio" value="range" v-model="durationFilterMode" />
              <span>Range</span>
            </label>
          </div>
          <div v-if="durationFilterMode === 'exact'" class="duration-filter-inputs">
            <input
              v-model.number="durationFilterExact"
              type="number"
              min="0"
              step="1"
              class="filter-popover-search"
              placeholder="Duration (seconds)"
            />
          </div>
          <div v-else class="duration-filter-inputs">
            <input
              v-model.number="durationFilterMin"
              type="number"
              min="0"
              step="1"
              class="filter-popover-search"
              placeholder="Min (seconds)"
            />
            <input
              v-model.number="durationFilterMax"
              type="number"
              min="0"
              step="1"
              class="filter-popover-search"
              placeholder="Max (seconds)"
            />
          </div>
        </div>
        <div class="filter-popover-footer">
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearDurationFilter">Clear</button>
          <button type="button" class="btn btn-sm btn-primary" @click="applyDurationFilter">Apply</button>
        </div>
        </div>
        <!-- Phone filter popover -->
        <div
          v-if="showPhoneFilterModal"
          ref="phoneFilterPopover"
          class="filter-popover"
          :style="phoneFilterPopoverStyle"
        >
        <div class="filter-popover-header">
          <span class="filter-popover-title">Filter by Phone Number</span>
          <button type="button" class="filter-popover-close" @click="closePhoneFilterModal" aria-label="Close">×</button>
        </div>
        <div class="filter-popover-body">
          <input
            v-model="phoneFilterTemp"
            type="text"
            class="filter-popover-search"
            placeholder="Phone (contains)..."
          />
        </div>
        <div class="filter-popover-footer">
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearPhoneFilter">Clear</button>
          <button type="button" class="btn btn-sm btn-primary" @click="applyPhoneFilter">Apply</button>
        </div>
        </div>
        <!-- First name filter popover -->
        <div
          v-if="showFirstNameFilterModal"
          ref="firstNameFilterPopover"
          class="filter-popover"
          :style="firstNameFilterPopoverStyle"
        >
        <div class="filter-popover-header">
          <span class="filter-popover-title">Filter by First Name</span>
          <button type="button" class="filter-popover-close" @click="closeFirstNameFilterModal" aria-label="Close">×</button>
        </div>
        <div class="filter-popover-body">
          <input
            v-model="firstNameFilterTemp"
            type="text"
            class="filter-popover-search"
            placeholder="First name (contains)..."
          />
        </div>
        <div class="filter-popover-footer">
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearFirstNameFilter">Clear</button>
          <button type="button" class="btn btn-sm btn-primary" @click="applyFirstNameFilter">Apply</button>
        </div>
        </div>
        <!-- Last name filter popover -->
        <div
          v-if="showLastNameFilterModal"
          ref="lastNameFilterPopover"
          class="filter-popover"
          :style="lastNameFilterPopoverStyle"
        >
        <div class="filter-popover-header">
          <span class="filter-popover-title">Filter by Last Name</span>
          <button type="button" class="filter-popover-close" @click="closeLastNameFilterModal" aria-label="Close">×</button>
        </div>
        <div class="filter-popover-body">
          <input
            v-model="lastNameFilterTemp"
            type="text"
            class="filter-popover-search"
            placeholder="Last name (contains)..."
          />
        </div>
        <div class="filter-popover-footer">
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearLastNameFilter">Clear</button>
          <button type="button" class="btn btn-sm btn-primary" @click="applyLastNameFilter">Apply</button>
        </div>
        </div>
      </Teleport>
      </article>
    </main>
  </div>
</template>

<script>
import DateRangePicker from '@/components/DateRangePicker.vue'
import UserMenu from '@/components/UserMenu.vue'
import AutoRefresh from '@/components/AutoRefresh.vue'

export default {
  components: { DateRangePicker, UserMenu, AutoRefresh },
  data() {
    return {
      isLoading: false,
      isExportingCsv: false,
      refreshRate: 0, 
      rows: [],
      selectedRange: null, 
      allStatuses: [],
      stateStatusTable: {}, 
      pieChartOptions: {
        chart: {
          type: 'pie',
          toolbar: { show: false }
        },
        series: [],
        labels: [],
        
        dataLabels: {
          enabled: true,
          formatter: function(val, opts) {
            // val — процент, opts.w.config.series — массив абсолютных значений
            return opts.w.config.series[opts.seriesIndex]; // показываем абсолютное значение
          }
        },
        tooltip: {
          enabled: true,
          y: {
            formatter: undefined  // НЕ указываем свою функцию
          }
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: { width: 300 },
            legend: { position: 'bottom' }
          }
        }]
      },
      pieChartSeries: [44, 55, 13],
      statePieOptions: {
        chart: {
          type: 'pie',
          toolbar: {
            show: false
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function (val, opts) {
            return opts.w.config.series[opts.seriesIndex]
          }
        },
        labels: [],
        responsive: [{
          breakpoint: 480,
          options: {
            chart: { width: 300 },
            legend: { position: 'bottom' }
          }
        }]
      },
      statePieSeries: [],
      summary: {},
      campaignOptions: [],
      insuranceOptions: [],
      statusOptions: [],
      statusNameOptions: [],
      totalItems: 0,
      showCharts: false,

      totalPages: 1, 
      currentPage: 1,
      pageSize: 100,
      userName: localStorage.getItem("username") || "User",
      roles: [],
      audioPlaying: {},
      showCampaignFilterModal: false,
      campaignFilterSearch: "",
      campaignFilterSelected: [],
      campaignFilterPopoverStyle: {},
      showStateFilterModal: false,
      stateFilterSearch: "",
      stateFilterSelected: [],
      stateFilterPopoverStyle: {},
      stateOptions: [],
      showStatusFilterModal: false,
      statusFilterSearch: "",
      statusFilterSelected: [],
      statusFilterPopoverStyle: {},
      showStatusNameFilterModal: false,
      statusNameFilterSearch: "",
      statusNameFilterSelected: [],
      statusNameFilterPopoverStyle: {},
      showInsuranceFilterModal: false,
      insuranceFilterSearch: "",
      insuranceFilterSelected: [],
      insuranceFilterPopoverStyle: {},
      showDurationFilterModal: false,
      durationFilterMode: "exact",
      durationFilterExact: null,
      durationFilterMin: null,
      durationFilterMax: null,
      durationFilterPopoverStyle: {},
      showPhoneFilterModal: false,
      phoneFilterTemp: "",
      phoneFilterPopoverStyle: {},
      showFirstNameFilterModal: false,
      firstNameFilterTemp: "",
      firstNameFilterPopoverStyle: {},
      showLastNameFilterModal: false,
      lastNameFilterTemp: "",
      lastNameFilterPopoverStyle: {},
      filterClickOutside: null,
      filters: {
        campaign_name: [],
        phone_number: "",
        state: [],
        status: [],
        status_name: [],
        call_duration:"",
        first_name:"",
        last_name:"",
        email:"",
        custom1:"",
        custom2: [],
        custom3:"",
        date_of_birth:"",
        length_in_sec: "",
        length_in_sec_min: "",
        length_in_sec_max: "",
        date_from: "",
        date_to: ""
      }
    };
  },
  computed: {
    filteredCampaignOptions() {
      const q = (this.campaignFilterSearch || "").trim().toLowerCase();
      if (!q) return this.campaignOptions || [];
      return (this.campaignOptions || []).filter((opt) =>
        String(opt || "").toLowerCase().includes(q)
      );
    },
    campaignFilterSelectedCount() {
      return Array.isArray(this.filters.campaign_name) ? this.filters.campaign_name.length : 0;
    },
    filteredStateOptions() {
      const q = (this.stateFilterSearch || "").trim().toLowerCase();
      if (!q) return this.stateOptions || [];
      return (this.stateOptions || []).filter((opt) =>
        String(opt || "").toLowerCase().includes(q)
      );
    },
    stateFilterSelectedCount() {
      return Array.isArray(this.filters.state) ? this.filters.state.length : 0;
    },
    filteredInsuranceOptions() {
      const q = (this.insuranceFilterSearch || "").trim().toLowerCase();
      if (!q) return this.insuranceOptions || [];
      return (this.insuranceOptions || []).filter((opt) =>
        String(opt || "").toLowerCase().includes(q)
      );
    },
    insuranceFilterSelectedCount() {
      return Array.isArray(this.filters.custom2) ? this.filters.custom2.length : 0;
    },
    filteredStatusOptions() {
      const q = (this.statusFilterSearch || "").trim().toLowerCase();
      if (!q) return this.statusOptions || [];
      return (this.statusOptions || []).filter((opt) =>
        String(opt || "").toLowerCase().includes(q)
      );
    },
    statusFilterSelectedCount() {
      return Array.isArray(this.filters.status) ? this.filters.status.length : 0;
    },
    filteredStatusNameOptions() {
      const q = (this.statusNameFilterSearch || "").trim().toLowerCase();
      if (!q) return this.statusNameOptions || [];
      return (this.statusNameOptions || []).filter((opt) =>
        String(opt || "").toLowerCase().includes(q)
      );
    },
    statusNameFilterSelectedCount() {
      return Array.isArray(this.filters.status_name) ? this.filters.status_name.length : 0;
    },
    durationFilterActive() {
      return (
        (this.filters.length_in_sec !== "" && this.filters.length_in_sec != null) ||
        (this.filters.length_in_sec_min !== "" && this.filters.length_in_sec_min != null) ||
        (this.filters.length_in_sec_max !== "" && this.filters.length_in_sec_max != null)
      );
    },
    phoneFilterActive() {
      return this.filters.phone_number !== "" && this.filters.phone_number != null;
    },
    first_nameFilterActive() {
      return (this.filters.first_name || "").toString().trim() !== "";
    },
    last_nameFilterActive() {
      return (this.filters.last_name || "").toString().trim() !== "";
    },
  },
  mounted() {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      return;
    }

    this.applyQuickRange('today');
    this.check_role();
    this.fetchCampaigns();
    this.fetchStates();
    this.fetchStatuses();
    this.fetchStatusNames();
    this.fetchInsurance();
    this.fetchSummary();
    document.title = "DNQ Report";
  },
  beforeUnmount() {
    if (this.filterClickOutside) {
      document.removeEventListener("click", this.filterClickOutside);
    }
  },
  methods: {
    formatDnqDate(value) {
      if (!value || value === "0000-00-00") return "";
      return value;
    },
    toggleCampaignFilterPopover() {
      if (this.showCampaignFilterModal) {
        this.closeCampaignFilterModal();
      } else {
        this.openCampaignFilterModal();
      }
    },
    openCampaignFilterModal() {
      this.closeStateFilterModal();
      this.closeStatusFilterModal();
      this.closeStatusNameFilterModal();
      this.closeFirstNameFilterModal();
      this.closeLastNameFilterModal();
      this.closeInsuranceFilterModal();
      this.closeDurationFilterModal();
      this.campaignFilterSelected = Array.isArray(this.filters.campaign_name)
        ? [...this.filters.campaign_name]
        : this.filters.campaign_name ? [this.filters.campaign_name] : [];
      this.campaignFilterSearch = "";
      this.showCampaignFilterModal = true;
      this.$nextTick(() => this.positionCampaignFilterPopover());
      this.addFilterClickOutside("campaign");
    },
    closeCampaignFilterModal() {
      this.showCampaignFilterModal = false;
      this.removeFilterClickOutside();
    },
    toggleStateFilterPopover() {
      if (this.showStateFilterModal) {
        this.closeStateFilterModal();
      } else {
        this.openStateFilterModal();
      }
    },
    openStateFilterModal() {
      this.closeCampaignFilterModal();
      this.closeStatusFilterModal();
      this.closeStatusNameFilterModal();
      this.closeFirstNameFilterModal();
      this.closeLastNameFilterModal();
      this.closeInsuranceFilterModal();
      this.closeDurationFilterModal();
      this.stateFilterSelected = Array.isArray(this.filters.state)
        ? [...this.filters.state]
        : this.filters.state ? [this.filters.state] : [];
      this.stateFilterSearch = "";
      this.showStateFilterModal = true;
      this.$nextTick(() => this.positionStateFilterPopover());
      this.addFilterClickOutside("state");
    },
    closeStateFilterModal() {
      this.showStateFilterModal = false;
      this.removeFilterClickOutside();
    },
    toggleStatusFilterPopover() {
      if (this.showStatusFilterModal) {
        this.closeStatusFilterModal();
      } else {
        this.openStatusFilterModal();
      }
    },
    openStatusFilterModal() {
      this.closeCampaignFilterModal();
      this.closeStateFilterModal();
      this.closeStatusNameFilterModal();
      this.closeFirstNameFilterModal();
      this.closeLastNameFilterModal();
      this.closeInsuranceFilterModal();
      this.closeDurationFilterModal();
      this.statusFilterSelected = Array.isArray(this.filters.status)
        ? [...this.filters.status]
        : this.filters.status ? [this.filters.status] : [];
      this.statusFilterSearch = "";
      this.showStatusFilterModal = true;
      this.$nextTick(() => this.positionStatusFilterPopover());
      this.addFilterClickOutside("status");
    },
    closeStatusFilterModal() {
      this.showStatusFilterModal = false;
      this.removeFilterClickOutside();
    },
    toggleStatusNameFilterPopover() {
      if (this.showStatusNameFilterModal) {
        this.closeStatusNameFilterModal();
      } else {
        this.openStatusNameFilterModal();
      }
    },
    openStatusNameFilterModal() {
      this.closeCampaignFilterModal();
      this.closeStateFilterModal();
      this.closeStatusFilterModal();
      this.closeInsuranceFilterModal();
      this.closeDurationFilterModal();
      this.statusNameFilterSelected = Array.isArray(this.filters.status_name)
        ? [...this.filters.status_name]
        : this.filters.status_name ? [this.filters.status_name] : [];
      this.statusNameFilterSearch = "";
      this.showStatusNameFilterModal = true;
      this.$nextTick(() => this.positionStatusNameFilterPopover());
      this.addFilterClickOutside("status_name");
    },
    closeStatusNameFilterModal() {
      this.showStatusNameFilterModal = false;
      this.removeFilterClickOutside();
    },
    toggleInsuranceFilterPopover() {
      if (this.showInsuranceFilterModal) {
        this.closeInsuranceFilterModal();
      } else {
        this.openInsuranceFilterModal();
      }
    },
    openInsuranceFilterModal() {
      this.closeCampaignFilterModal();
      this.closeStateFilterModal();
      this.closeStatusFilterModal();
      this.closeStatusNameFilterModal();
      this.closeDurationFilterModal();
      this.insuranceFilterSelected = Array.isArray(this.filters.custom2)
        ? [...this.filters.custom2]
        : this.filters.custom2 ? [this.filters.custom2] : [];
      this.insuranceFilterSearch = "";
      this.showInsuranceFilterModal = true;
      this.$nextTick(() => this.positionInsuranceFilterPopover());
      this.addFilterClickOutside("insurance");
    },
    closeInsuranceFilterModal() {
      this.showInsuranceFilterModal = false;
      this.removeFilterClickOutside();
    },
    positionInsuranceFilterPopover() {
      const btn = this.$refs.insuranceFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 320;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.insuranceFilterPopoverStyle = {
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
      };
    },
    positionStateFilterPopover() {
      const btn = this.$refs.stateFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 320;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.stateFilterPopoverStyle = {
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
      };
    },
    positionStatusFilterPopover() {
      const btn = this.$refs.statusFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 320;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.statusFilterPopoverStyle = {
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
      };
    },
    positionStatusNameFilterPopover() {
      const btn = this.$refs.statusNameFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 320;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.statusNameFilterPopoverStyle = {
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
      };
    },
    addFilterClickOutside(type) {
      this.removeFilterClickOutside();
      setTimeout(() => {
        this.filterClickOutside = (e) => {
          if (type === "campaign") {
            const popover = this.$refs.campaignFilterPopover;
            const btn = this.$refs.campaignFilterBtn;
            if (popover && popover.contains(e.target)) return;
            if (btn && btn.contains(e.target)) return;
            this.closeCampaignFilterModal();
          } else if (type === "state") {
            const popover = this.$refs.stateFilterPopover;
            const btn = this.$refs.stateFilterBtn;
            if (popover && popover.contains(e.target)) return;
            if (btn && btn.contains(e.target)) return;
            this.closeStateFilterModal();
          } else if (type === "status") {
            const popover = this.$refs.statusFilterPopover;
            const btn = this.$refs.statusFilterBtn;
            if (popover && popover.contains(e.target)) return;
            if (btn && btn.contains(e.target)) return;
            this.closeStatusFilterModal();
          } else if (type === "status_name") {
            const popover = this.$refs.statusNameFilterPopover;
            const btn = this.$refs.statusNameFilterBtn;
            if (popover && popover.contains(e.target)) return;
            if (btn && btn.contains(e.target)) return;
            this.closeStatusNameFilterModal();
          } else if (type === "insurance") {
            const popover = this.$refs.insuranceFilterPopover;
            const btn = this.$refs.insuranceFilterBtn;
            if (popover && popover.contains(e.target)) return;
            if (btn && btn.contains(e.target)) return;
            this.closeInsuranceFilterModal();
          } else if (type === "phone") {
            const popover = this.$refs.phoneFilterPopover;
            const btn = this.$refs.phoneFilterBtn;
            if (popover && popover.contains(e.target)) return;
            if (btn && btn.contains(e.target)) return;
            this.closePhoneFilterModal();
          } else if (type === "first_name") {
            const popover = this.$refs.firstNameFilterPopover;
            const btn = this.$refs.firstNameFilterBtn;
            if (popover && popover.contains(e.target)) return;
            if (btn && btn.contains(e.target)) return;
            this.closeFirstNameFilterModal();
          } else if (type === "last_name") {
            const popover = this.$refs.lastNameFilterPopover;
            const btn = this.$refs.lastNameFilterBtn;
            if (popover && popover.contains(e.target)) return;
            if (btn && btn.contains(e.target)) return;
            this.closeLastNameFilterModal();
          } else if (type === "duration") {
            const popover = this.$refs.durationFilterPopover;
            const btn = this.$refs.durationFilterBtn;
            if (popover && popover.contains(e.target)) return;
            if (btn && btn.contains(e.target)) return;
            this.closeDurationFilterModal();
          }
        };
        document.addEventListener("click", this.filterClickOutside);
      }, 0);
    },
    removeFilterClickOutside() {
      if (this.filterClickOutside) {
        document.removeEventListener("click", this.filterClickOutside);
        this.filterClickOutside = null;
      }
    },
    positionCampaignFilterPopover() {
      const btn = this.$refs.campaignFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 320;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.campaignFilterPopoverStyle = {
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
      };
    },
    applyCampaignFilter() {
      this.filters.campaign_name = [...this.campaignFilterSelected];
      this.closeCampaignFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    applyStateFilter() {
      this.filters.state = [...this.stateFilterSelected];
      this.closeStateFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    applyStatusFilter() {
      this.filters.status = [...this.statusFilterSelected];
      this.closeStatusFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    applyStatusNameFilter() {
      this.filters.status_name = [...this.statusNameFilterSelected];
      this.closeStatusNameFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    selectAllCampaigns() {
      this.campaignFilterSelected = [...this.filteredCampaignOptions];
    },
    deselectAllCampaigns() {
      this.campaignFilterSelected = [];
    },
    selectAllStates() {
      this.stateFilterSelected = [...this.filteredStateOptions];
    },
    deselectAllStates() {
      this.stateFilterSelected = [];
    },
    selectAllStatuses() {
      this.statusFilterSelected = [...this.filteredStatusOptions];
    },
    deselectAllStatuses() {
      this.statusFilterSelected = [];
    },
    selectAllStatusNames() {
      this.statusNameFilterSelected = [...this.filteredStatusNameOptions];
    },
    deselectAllStatusNames() {
      this.statusNameFilterSelected = [];
    },
    applyInsuranceFilter() {
      this.filters.custom2 = [...this.insuranceFilterSelected];
      this.closeInsuranceFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    selectAllInsurance() {
      this.insuranceFilterSelected = [...this.filteredInsuranceOptions];
    },
    deselectAllInsurance() {
      this.insuranceFilterSelected = [];
    },
    toggleDurationFilterPopover() {
      if (this.showDurationFilterModal) {
        this.closeDurationFilterModal();
      } else {
        this.openDurationFilterModal();
      }
    },
    openDurationFilterModal() {
      this.closeCampaignFilterModal();
      this.closeStateFilterModal();
      this.closeStatusFilterModal();
      this.closeStatusNameFilterModal();
      this.closeFirstNameFilterModal();
      this.closeLastNameFilterModal();
      this.closeInsuranceFilterModal();
      this.durationFilterMode = (this.filters.length_in_sec !== "" && this.filters.length_in_sec != null) ? "exact" : "range";
      this.durationFilterExact = this.filters.length_in_sec !== "" && this.filters.length_in_sec != null ? parseFloat(this.filters.length_in_sec) : null;
      this.durationFilterMin = this.filters.length_in_sec_min !== "" && this.filters.length_in_sec_min != null ? parseFloat(this.filters.length_in_sec_min) : null;
      this.durationFilterMax = this.filters.length_in_sec_max !== "" && this.filters.length_in_sec_max != null ? parseFloat(this.filters.length_in_sec_max) : null;
      this.showDurationFilterModal = true;
      this.$nextTick(() => this.positionDurationFilterPopover());
      this.addFilterClickOutside("duration");
    },
    togglePhoneFilterPopover() {
      if (this.showPhoneFilterModal) {
        this.closePhoneFilterModal();
      } else {
        this.openPhoneFilterModal();
      }
    },
    openPhoneFilterModal() {
      this.closeFirstNameFilterModal();
      this.closeLastNameFilterModal();
      this.phoneFilterTemp = this.filters.phone_number || "";
      this.showPhoneFilterModal = true;
      this.$nextTick(() => this.positionPhoneFilterPopover());
      this.addFilterClickOutside("phone");
    },
    closePhoneFilterModal() {
      this.showPhoneFilterModal = false;
      this.removeFilterClickOutside();
    },
    positionPhoneFilterPopover() {
      const btn = this.$refs.phoneFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 280;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.phoneFilterPopoverStyle = {
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
      };
    },
    applyPhoneFilter() {
      this.filters.phone_number = (this.phoneFilterTemp || "").trim();
      this.closePhoneFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    clearPhoneFilter() {
      this.phoneFilterTemp = "";
      this.filters.phone_number = "";
      this.closePhoneFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    toggleFirstNameFilterPopover() {
      if (this.showFirstNameFilterModal) {
        this.closeFirstNameFilterModal();
      } else {
        this.openFirstNameFilterModal();
      }
    },
    openFirstNameFilterModal() {
      this.closeCampaignFilterModal();
      this.closeStateFilterModal();
      this.closeStatusFilterModal();
      this.closeStatusNameFilterModal();
      this.closePhoneFilterModal();
      this.closeInsuranceFilterModal();
      this.closeDurationFilterModal();
      this.firstNameFilterTemp = this.filters.first_name || "";
      this.showFirstNameFilterModal = true;
      this.$nextTick(() => this.positionFirstNameFilterPopover());
      this.addFilterClickOutside("first_name");
    },
    closeFirstNameFilterModal() {
      this.showFirstNameFilterModal = false;
      this.removeFilterClickOutside();
    },
    positionFirstNameFilterPopover() {
      const btn = this.$refs.firstNameFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 280;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.firstNameFilterPopoverStyle = {
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
      };
    },
    applyFirstNameFilter() {
      this.filters.first_name = (this.firstNameFilterTemp || "").trim();
      this.closeFirstNameFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    clearFirstNameFilter() {
      this.firstNameFilterTemp = "";
      this.filters.first_name = "";
      this.closeFirstNameFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    toggleLastNameFilterPopover() {
      if (this.showLastNameFilterModal) {
        this.closeLastNameFilterModal();
      } else {
        this.openLastNameFilterModal();
      }
    },
    openLastNameFilterModal() {
      this.closeCampaignFilterModal();
      this.closeStateFilterModal();
      this.closeStatusFilterModal();
      this.closeStatusNameFilterModal();
      this.closePhoneFilterModal();
      this.closeInsuranceFilterModal();
      this.closeDurationFilterModal();
      this.lastNameFilterTemp = this.filters.last_name || "";
      this.showLastNameFilterModal = true;
      this.$nextTick(() => this.positionLastNameFilterPopover());
      this.addFilterClickOutside("last_name");
    },
    closeLastNameFilterModal() {
      this.showLastNameFilterModal = false;
      this.removeFilterClickOutside();
    },
    positionLastNameFilterPopover() {
      const btn = this.$refs.lastNameFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 280;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.lastNameFilterPopoverStyle = {
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
      };
    },
    applyLastNameFilter() {
      this.filters.last_name = (this.lastNameFilterTemp || "").trim();
      this.closeLastNameFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    clearLastNameFilter() {
      this.lastNameFilterTemp = "";
      this.filters.last_name = "";
      this.closeLastNameFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    closeDurationFilterModal() {
      this.showDurationFilterModal = false;
      this.removeFilterClickOutside();
    },
    positionDurationFilterPopover() {
      const btn = this.$refs.durationFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 320;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.durationFilterPopoverStyle = {
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
      };
    },
    applyDurationFilter() {
      if (this.durationFilterMode === "exact") {
        const val = this.durationFilterExact;
        this.filters.length_in_sec = (val !== null && val !== undefined && !isNaN(val)) ? String(val) : "";
        this.filters.length_in_sec_min = "";
        this.filters.length_in_sec_max = "";
      } else {
        const min = this.durationFilterMin;
        const max = this.durationFilterMax;
        this.filters.length_in_sec = "";
        this.filters.length_in_sec_min = (min !== null && min !== undefined && !isNaN(min)) ? String(min) : "";
        this.filters.length_in_sec_max = (max !== null && max !== undefined && !isNaN(max)) ? String(max) : "";
      }
      this.closeDurationFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    clearDurationFilter() {
      this.durationFilterExact = null;
      this.durationFilterMin = null;
      this.durationFilterMax = null;
      this.filters.length_in_sec = "";
      this.filters.length_in_sec_min = "";
      this.filters.length_in_sec_max = "";
      this.closeDurationFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    async fetchStates() {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (this.filters.date_from) params.set("date_from", this.filters.date_from);
      if (this.filters.date_to) params.set("date_to", this.filters.date_to);
      const res = await fetch(`/api/data/states?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      this.stateOptions = data.items || [];
    },
    async fetchStatuses() {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (this.filters.date_from) params.set("date_from", this.filters.date_from);
      if (this.filters.date_to) params.set("date_to", this.filters.date_to);
      const res = await fetch(`/api/data/statuses?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      this.statusOptions = data.items || [];
    },
    async fetchStatusNames() {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (this.filters.date_from) params.set("date_from", this.filters.date_from);
      if (this.filters.date_to) params.set("date_to", this.filters.date_to);
      const res = await fetch(`/api/data/status_names?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      this.statusNameOptions = data.items || [];
    },
    clearFilters() {
      this.filters.campaign_name = [];
      this.filters.state = [];
      this.filters.custom2 = [];
      this.filters.phone_number = "";
      this.filters.status = [];
      this.filters.status_name = [];
      this.filters.first_name = "";
      this.filters.last_name = "";
      this.filters.email = "";
      this.filters.custom1 = "";
      this.filters.custom3 = "";
      this.filters.date_of_birth = "";
      this.filters.length_in_sec = "";
      this.filters.length_in_sec_min = "";
      this.filters.length_in_sec_max = "";
      this.currentPage = 1;
      this.fetchData();
    },
    async downloadSummaryCSV() {
        if (this.isExportingCsv) return;
        if (!this.stateStatusTable || !Object.keys(this.stateStatusTable).length) {
          return;
        }

        this.isExportingCsv = true;
        try {
          await this.$nextTick();

        let csv = "\uFEFF"; // BOM для Excel

        // Заголовок
        csv += "STATE," + this.allStatuses.join(",") + "\n";

        // Строки
        Object.entries(this.stateStatusTable).forEach(([state, statuses]) => {
          const row = [state];

          this.allStatuses.forEach(status => {
            row.push(statuses[status] || 0);
          });

          csv += row.join(",") + "\n";
        });

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = "dnq_summary.csv";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
        } finally {
          this.isExportingCsv = false;
        }
      },
    onDateRangeChange({ dateFrom, dateTo }) {
      this.filters.date_from = dateFrom;
      this.filters.date_to   = dateTo;
      this.currentPage = 1;
      this.fetchCampaigns();
      this.fetchStates();
      this.fetchStatuses();
      this.fetchStatusNames();
      this.fetchInsurance();
      this.fetchData();
    },
    onTopDateChange(value) {
    if (value && value.length === 2) {
      this.filters.date_from = value[0].toISOString().slice(0, 19).replace('T', ' ');
      this.filters.date_to = value[1].toISOString().slice(0, 19).replace('T', ' ');
    } else {
      this.filters.date_from = "";
      this.filters.date_to = "";
    }
    this.currentPage = 1;
    this.fetchData();
  },
    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.fetchData();
    },
    async fetchCampaigns() {
        const token = localStorage.getItem("token");
        const params = new URLSearchParams();
        if (this.filters.date_from) params.set("date_from", this.filters.date_from);
        if (this.filters.date_to) params.set("date_to", this.filters.date_to);
        const res = await fetch(`/api/campaigns?${params.toString()}`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        this.campaignOptions = data.items || [];
    },
    async fetchInsurance() {
        const token = localStorage.getItem("token");
        const params = new URLSearchParams();
        if (this.filters.date_from) params.set("date_from", this.filters.date_from);
        if (this.filters.date_to) params.set("date_to", this.filters.date_to);
        const res = await fetch(`/api/insurance?${params.toString()}`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        this.insuranceOptions = data.items || [];
    },
    async downloadRecording(row) {
      const filename = row.recording_file;
      await fetch("/api/logs", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + localStorage.getItem("token")
        },
        body: JSON.stringify({
          action_type: `download_recording ${filename}`,
          username: localStorage.getItem("username")
        })
      });
      const link = document.createElement("a");
      link.href = "/audio/" + filename;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    updateStatePieFromRows() {
      if (!this.chartRows || this.chartRows.length === 0) {
        this.statePieSeries = [];
        this.statePieOptions = {
          ...this.statePieOptions,
          labels: []
        };
        console.log("check")
        console.log(this.statePieSeries)
        console.log(this.statePieOptions)
        return;
      }

      const stateCounts = {};

      this.chartRows.forEach(row => {
        const state = row.state && row.state.trim() !== "" 
          ? row.state 
          : "Unknown";

        if (!stateCounts[state]) {
          stateCounts[state] = 0;
        }
        stateCounts[state]++;
      });

      const labels = Object.keys(stateCounts);
      const series = Object.values(stateCounts);

      this.statePieOptions = {
        ...this.statePieOptions,
        labels
      };

      this.statePieSeries = series;
      console.log("this.statePieSeries")
      console.log(this.statePieSeries)
    },
    applyQuickRange(range) {
      const getNow = () => {
        const nyStr = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });
        return new Date(nyStr);
      };

      const now = getNow();

      const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
      const endOfDay   = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);

      let from, to = getNow();

      switch (range) {
        case 'last5m':   from = new Date(now - 5*60*1000); break;
        case 'last15m':  from = new Date(now - 15*60*1000); break;
        case 'last30m':  from = new Date(now - 30*60*1000); break;
        case 'last1h':   from = new Date(now - 60*60*1000); break;
        case 'last3h':   from = new Date(now - 3*60*60*1000); break;
        case 'last6h':   from = new Date(now - 6*60*60*1000); break;
        case 'last12h':  from = new Date(now - 12*60*60*1000); break;
        case 'last24h':  from = new Date(now - 24*60*60*1000); break;
        case 'last2d':   from = new Date(now - 2*24*60*60*1000); break;
        case 'last7d':   from = new Date(now - 7*24*60*60*1000); break;
        case 'last30d':  from = new Date(now - 30*24*60*60*1000); break;
        case 'last90d':  from = new Date(now - 90*24*60*60*1000); break;
        case 'today':
          from = startOfDay(now); to = endOfDay(now); break;
        case 'yesterday': {
          const y = new Date(now); y.setDate(y.getDate() - 1);
          from = startOfDay(y); to = endOfDay(y); break;
        }
        case 'thisWeek': {
          const d = now.getDay(), diff = (d === 0 ? -6 : 1) - d;
          from = startOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() + diff));
          to = endOfDay(now); break;
        }
        case 'lastWeek': {
          const d = now.getDay(), diff = (d === 0 ? -6 : 1) - d;
          const monThis = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diff);
          from = startOfDay(new Date(monThis - 7*24*60*60*1000));
          to   = endOfDay(new Date(monThis - 24*60*60*1000)); break;
        }
        case 'thisMonth':
          from = new Date(now.getFullYear(), now.getMonth(), 1);
          to   = endOfDay(now); break;
        case 'lastMonth':
          from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          to   = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59); break;
        case 'thisYear':
          from = new Date(now.getFullYear(), 0, 1);
          to   = endOfDay(now); break;
        case 'lastYear':
          from = new Date(now.getFullYear() - 1, 0, 1);
          to   = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59); break;
        default: return;
      }

      // Форматируем дату как обычные цифры без конвертаций
      const fmt = (d) => {
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      };

      this.selectedRange = [from, to];
      this.filters.date_from = fmt(from);
      this.filters.date_to   = fmt(to);
      this.currentPage = 1;
      this.fetchData();
    },

        
    async fetchSummary() {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch("https://dnqreport.noc.solutions/api/summary", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        this.summary = await res.json();
      } catch (e) {
        console.error("Summary load error:", e);
      }
    },
    async toggleAudio(i, row) {
      const audioId = `audio-${row.id || i}`;
      const audio = document.getElementById(audioId);
      
      if (!audio) {
        return;
      }
      
      if (this.audioPlaying[i]) {
        // Пауза
        audio.pause();
        this.audioPlaying[i] = false;
      } else {
        // Останавливаем все другие аудио перед воспроизведением нового
        Object.keys(this.audioPlaying).forEach(key => {
          if (this.audioPlaying[key] && key !== i) {
            const otherRow = this.rows[parseInt(key)];
            if (otherRow) {
              const otherAudioId = `audio-${otherRow.id || key}`;
              const otherAudio = document.getElementById(otherAudioId);
              if (otherAudio) {
                otherAudio.pause();
                this.audioPlaying[key] = false;
              }
            }
          }
        });
        
        // Воспроизведение
        const filename = row.recording_file;
        await fetch("/api/logs", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer " + localStorage.getItem("token")
          },
          body: JSON.stringify({
            action_type: `play_recording ${filename}`,
            username: localStorage.getItem("username")
          })
        });
        
        audio.play().catch(err => {
          console.error("Error playing audio:", err);
          this.audioPlaying[i] = false;
        });
        this.audioPlaying[i] = true;
      }
    },
    audioEnded(i) {
      this.audioPlaying[i] = false;
    },
    audioError(i) {
      this.audioPlaying[i] = false;
    },
    async check_role(){
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ user: this.userName });
      const res = await fetch(`/api/role?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      this.roles = data.roles;
      return this.roles
    },
    async fetchData() {
      const token = localStorage.getItem('token');
      if (!token) {
        this.$router.push('/login');
        return;
      }

      // Останавливаем все воспроизводимые аудио при загрузке новых данных
      Object.keys(this.audioPlaying).forEach(key => {
        if (this.audioPlaying[key]) {
          const row = this.rows[parseInt(key)];
          if (row) {
            const audioId = `audio-${row.id || key}`;
            const audio = document.getElementById(audioId);
            if (audio) {
              audio.pause();
            }
          }
          this.audioPlaying[key] = false;
        }
      });

      this.isLoading = true;
      await this.$nextTick();

      const cleanFilters = {};
      Object.keys(this.filters).forEach(key => {
        if (key === "campaign_name" || key === "state" || key === "custom2" || key === "status" || key === "status_name" || key === "length_in_sec" || key === "length_in_sec_min" || key === "length_in_sec_max") return;
        cleanFilters[key] = this.filters[key] === null ? '' : this.filters[key];
      });
      const params = new URLSearchParams({
        page: this.currentPage,
        page_size: this.pageSize,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...cleanFilters
      });
      const campaignNames = this.filters.campaign_name;
      if (Array.isArray(campaignNames) && campaignNames.length > 0) {
        campaignNames.forEach((c) => params.append("campaign_name", c));
      } else if (campaignNames && !Array.isArray(campaignNames)) {
        params.set("campaign_name", campaignNames);
      }
      const states = this.filters.state;
      if (Array.isArray(states) && states.length > 0) {
        states.forEach((s) => params.append("state", s));
      } else if (states && !Array.isArray(states)) {
        params.set("state", states);
      }
      const statuses = this.filters.status;
      if (Array.isArray(statuses) && statuses.length > 0) {
        statuses.forEach((s) => params.append("status", s));
      } else if (statuses && !Array.isArray(statuses)) {
        params.set("status", statuses);
      }
      const statusNames = this.filters.status_name;
      if (Array.isArray(statusNames) && statusNames.length > 0) {
        statusNames.forEach((s) => params.append("status_name", s));
      } else if (statusNames && !Array.isArray(statusNames)) {
        params.set("status_name", statusNames);
      }
      const insuranceCompanies = this.filters.custom2;
      if (Array.isArray(insuranceCompanies) && insuranceCompanies.length > 0) {
        insuranceCompanies.forEach((c) => params.append("custom2", c));
      } else if (insuranceCompanies && !Array.isArray(insuranceCompanies)) {
        params.set("custom2", insuranceCompanies);
      }
      if (this.filters.length_in_sec !== "" && this.filters.length_in_sec != null) {
        params.set("length_in_sec", this.filters.length_in_sec);
      }
      if (this.filters.length_in_sec_min !== "" && this.filters.length_in_sec_min != null) {
        params.set("length_in_sec_min", this.filters.length_in_sec_min);
      }
      if (this.filters.length_in_sec_max !== "" && this.filters.length_in_sec_max != null) {
        params.set("length_in_sec_max", this.filters.length_in_sec_max);
      }

      try {
        const res = await fetch(`/api/data?${params.toString()}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.status === 401) {
          localStorage.removeItem('token');
          this.$router.push('/login');
          return;
        }
        const data = await res.json();
        this.rows = data.items || [];
        this.totalItems = data.total || 0;
        this.totalPages = Math.ceil(this.totalItems / this.pageSize);

        const chartParams = new URLSearchParams(cleanFilters);
        if (Array.isArray(campaignNames) && campaignNames.length > 0) {
          campaignNames.forEach((c) => chartParams.append("campaign_name", c));
        }
        if (Array.isArray(states) && states.length > 0) {
          states.forEach((s) => chartParams.append("state", s));
        }
        if (Array.isArray(statuses) && statuses.length > 0) {
          statuses.forEach((s) => chartParams.append("status", s));
        }
        if (Array.isArray(statusNames) && statusNames.length > 0) {
          statusNames.forEach((s) => chartParams.append("status_name", s));
        }
        if (Array.isArray(insuranceCompanies) && insuranceCompanies.length > 0) {
          insuranceCompanies.forEach((c) => chartParams.append("custom2", c));
        }
        if (this.filters.length_in_sec !== "" && this.filters.length_in_sec != null) {
          chartParams.set("length_in_sec", this.filters.length_in_sec);
        }
        if (this.filters.length_in_sec_min !== "" && this.filters.length_in_sec_min != null) {
          chartParams.set("length_in_sec_min", this.filters.length_in_sec_min);
        }
        if (this.filters.length_in_sec_max !== "" && this.filters.length_in_sec_max != null) {
          chartParams.set("length_in_sec_max", this.filters.length_in_sec_max);
        }
        const chartRes = await fetch(`/api/data/full?${chartParams.toString()}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const chartData = await chartRes.json();

        this.allStatuses = Array.from(new Set(Object.keys(chartData.statusCounts).map(s => s.trim().toUpperCase())));

        this.stateStatusTable = {};
        for (const [state, statuses] of Object.entries(chartData.stateStatusCounts)) {
          const stateKey = state.trim().toUpperCase() || "UNKNOWN";
          this.stateStatusTable[stateKey] = {};

          // Заполняем все статусы, даже если их нет для этого штата
          this.allStatuses.forEach(status => {
            this.stateStatusTable[stateKey][status] = statuses[status] || 0;
          });
        }

        const t = Object.keys(chartData.statusCounts).map(label => {
          return label.trim().toUpperCase();
        });
        const a = Object.keys(chartData.stateCounts).map(label => {
          return label.trim().toUpperCase();
        });
        this.pieChartSeries = Object.values(chartData.statusCounts);
        this.statePieSeries = Object.values(chartData.stateCounts);

        this.pieChartOptions = {
          labels: t
        };
        this.statePieOptions = {
          labels: a
        };

        return data;
      } catch (e) {
        console.error("Fetch error:", e);
      } finally {
        this.isLoading = false;
      }
    },
    async downloadCSV() {
        if (this.isExportingCsv) return;

        try {
          this.isExportingCsv = true;
          const params = new URLSearchParams();
        Object.keys(this.filters).forEach((key) => {
          if (key === "campaign_name" || key === "state" || key === "custom2" || key === "status" || key === "status_name" || key === "length_in_sec" || key === "length_in_sec_min" || key === "length_in_sec_max") return;
          const v = this.filters[key];
          if (v != null && v !== "") params.set(key, v);
        });
        const cn = this.filters.campaign_name;
        if (Array.isArray(cn) && cn.length > 0) {
          cn.forEach((c) => params.append("campaign_name", c));
        } else if (cn && !Array.isArray(cn)) {
          params.set("campaign_name", cn);
        }
        const st = this.filters.state;
        if (Array.isArray(st) && st.length > 0) {
          st.forEach((s) => params.append("state", s));
        } else if (st && !Array.isArray(st)) {
          params.set("state", st);
        }
        const statuses = this.filters.status;
        if (Array.isArray(statuses) && statuses.length > 0) {
          statuses.forEach((s) => params.append("status", s));
        } else if (statuses && !Array.isArray(statuses)) {
          params.set("status", statuses);
        }
        const statusNames = this.filters.status_name;
        if (Array.isArray(statusNames) && statusNames.length > 0) {
          statusNames.forEach((s) => params.append("status_name", s));
        } else if (statusNames && !Array.isArray(statusNames)) {
          params.set("status_name", statusNames);
        }
        const ins = this.filters.custom2;
        if (Array.isArray(ins) && ins.length > 0) {
          ins.forEach((c) => params.append("custom2", c));
        } else if (ins && !Array.isArray(ins)) {
          params.set("custom2", ins);
        }
        if (this.filters.length_in_sec !== "" && this.filters.length_in_sec != null) {
          params.set("length_in_sec", this.filters.length_in_sec);
        }
        if (this.filters.length_in_sec_min !== "" && this.filters.length_in_sec_min != null) {
          params.set("length_in_sec_min", this.filters.length_in_sec_min);
        }
        if (this.filters.length_in_sec_max !== "" && this.filters.length_in_sec_max != null) {
          params.set("length_in_sec_max", this.filters.length_in_sec_max);
        }

          await fetch("/api/logs", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": "Bearer " + localStorage.getItem("token")
            },
            body: JSON.stringify({
              action_type: "download_csv",
              username: localStorage.getItem("username")
            })
          });

          // The export is streamed and can be hundreds of MB for a year, so we don't
          // fetch() it into a Blob (that blocks until the whole body is in memory).
          // Instead we get a short-lived download token and hand the URL to the
          // browser's native download manager, which streams straight to disk.
          const tokenRes = await fetch("/api/data/csv/token", {
            method: "POST",
            headers: { "Authorization": "Bearer " + localStorage.getItem("token") }
          });
          if (!tokenRes.ok) {
            const body = await tokenRes.json().catch(() => ({}));
            throw new Error(body.error || `HTTP ${tokenRes.status}`);
          }
          const { download_token } = await tokenRes.json();
          if (!download_token) throw new Error("No download token received");

          params.set("dl", download_token);

          const link = document.createElement("a");
          link.href = `/api/data/csv?${params.toString()}`;
          link.download = "dnq_report.csv";
          link.rel = "noopener";
          document.body.appendChild(link);
          link.click();
          link.remove();
        } catch (e) {
          console.error("error CSV:", e);
          alert(`Failed to download CSV: ${e.message || e}`);
        } finally {
          // The browser now owns the download; we only know it started.
          setTimeout(() => { this.isExportingCsv = false; }, 1500);
        }
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.currentPage = 1; // сбрасываем на первую страницу при фильтре
        this.fetchData();
      }
    },
  }
};
</script>

<style scoped>

#reportPage {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: linear-gradient(135deg, #1e3c72, #2a5298);
  padding: 20px 10px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

#reportPage main {
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

#reportPage article {
  background: #fff;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  /* Hard stop: nothing (CSV, wide table) can paint onto the blue page bg */
  overflow-x: hidden;
}

.title-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
}

.login-title {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  color: #4e54c8;
}

.btn-submit {
  width: 200px;
  max-width: 100%;
  display: inline-block;
  background: linear-gradient(135deg, #6a11cb, #2575fc);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease, transform 0.2s ease;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.btn-submit:hover {
  background: linear-gradient(135deg, #5a0fc1, #1e63d6);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
}

.btn-submit:active {
  transform: translateY(1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.v-select {
  font-size: 12px;
  border-radius: 8px;
  font-weight: normal;
}

.v-select .vs__dropdown-toggle {
  min-height: 32px;
  font-size: 12px;
  font-weight: normal;
}

.v-select .vs__selected {
  font-size: 12px;
  font-weight: normal;
}


.table-responsive table {
  min-width: 700px;
  -webkit-overflow-scrolling: touch;
  table-layout: fixed;
  width: 100%
}

table {
  width: 100%;
  table-layout: auto;
  font-size: 10px;
}

th, td {
  vertical-align: middle !important;
}

.dnq-data-table thead th {
  white-space: nowrap;
  font-size: 10px;
  line-height: 1.2;
  padding: 8px 10px;
  vertical-align: bottom !important;
}

.dnq-data-table tbody td {
  word-wrap: break-word;
  white-space: normal;
}

.dnq-data-table .campaign-header-cell-inner {
  white-space: nowrap;
  flex-wrap: nowrap;
}

.filter-input,
.v-select .vs__dropdown-toggle {
  height: 32px;
  min-height: 32px;
  font-size: 10px;
  padding: 6px 8px;
  font-weight: normal;
}

.table-responsive {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-responsive table.dnq-data-table {
  width: 100%;
  min-width: 100%;
  table-layout: auto;
}

.dnq-data-table th,
.dnq-data-table td {
  max-width: none;
}

.recording-cell {
  white-space: nowrap;
  width: 90px;
}

.recording-cell-actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

@media (max-width: 768px) {
  .table-responsive table.dnq-data-table {
    min-width: 900px;
  }
}
@media (max-width: 768px) {
  .login-title { font-size: 22px; }
  table { font-size: 12px; }
  .filter-input { font-size: 11px; padding: 4px 6px; }
}

.dnq-main-table-section {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  clear: both;
  margin-top: 12px;
  box-sizing: border-box;
  overflow-x: hidden;
}

.statuses-card-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
}

.statuses-card-csv {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

@media (max-width: 640px) {
  .statuses-card-header {
    flex-direction: column;
    gap: 8px;
  }

  .statuses-card-csv {
    position: static;
    transform: none;
    align-self: flex-end;
  }
}

.table-top-filters {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px 12px;
  margin-bottom: 12px;
  margin-top: 0;
  flex-wrap: wrap;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.table-top-filters .link-csv {
  flex: 0 0 auto;
}

.table-top-filters :deep(.date-range-picker-wrapper) {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  flex: 0 1 auto;
}

.table-top-filters :deep(.grafana-datepicker) {
  width: 300px !important;
  max-width: 100%;
  min-width: 0;
  flex: 1 1 auto;
}

.table-top-filters :deep(.dp__input_wrap),
.table-top-filters :deep(.dp__input) {
  max-width: 100%;
  box-sizing: border-box;
}

.table-top-filters :deep(.auto-refresh-wrapper) {
  flex: 0 0 auto;
}

.view-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toggle-label {
  font-size: 13px;
  font-weight: 500;
  color: #aaa;
  transition: color 0.2s;
}

.toggle-label.active {
  color: #4e54c8;
  font-weight: 700;
}

.toggle-switch {
  width: 48px;
  height: 24px;
  background: #dde1f5;
  border-radius: 12px;
  cursor: pointer;
  position: relative;
  transition: background 0.2s;
}
.toggle-knob {
  width: 20px;
  height: 20px;
  background: #4e54c8;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  transition: left 0.2s;
}
.multiselect.filter-input,
.multiselect.filter-input .multiselect-wrapper {
  border-radius: 12px !important;
}

/* Чтобы бордер-радиус не ломался при открытии */
.multiselect.filter-input[aria-expanded="true"] .multiselect-wrapper {
  border-radius: 12px !important;
}

/* Если используешь внутренний placeholder/selected */
.multiselect.filter-input .multiselect-placeholder,
.multiselect.filter-input .multiselect-selected {
  border-radius: 12px !important;
}
.toggle-knob.left  { left: 2px; }
.toggle-knob.right { left: 26px; }

.link-csv {
  font-size: 12px;
  color: #4f46e5;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
  flex: 0 0 auto;
}
.link-csv:hover {
  text-decoration: underline;
}

.link-csv--disabled {
  opacity: 0.6;
  pointer-events: none;
  cursor: wait;
}

.audio-btn {
  width: 18px;
  height: 18px;
  min-width: 18px;
  flex-shrink: 0;
  border-radius: 50%;
  padding: 0;
  font-size: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
}

.audio-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.download-btn {
  background-color: #28a745; /* зелёная для скачивания */
  color: white;
}

.play-btn {
  background-color: #007bff; /* синяя для проигрывания */
  color: white;
}

/* All header labels in second row align to bottom */
thead tr:last-child th {
  vertical-align: bottom !important;
}
/* Campaign header with filter icon */
.campaign-header-cell {
  vertical-align: bottom !important;
}
.campaign-header-cell-inner {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.campaign-header-cell span {
  font-weight: 600;
}
/* Filter icon - no frame, just icon */
.filter-icon-btn {
  position: relative;
  width: auto;
  height: auto;
  padding: 2px 4px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}
.filter-icon-btn:hover {
  color: #4e54c8;
}
.filter-icon-btn.active {
  color: #4e54c8;
}
.filter-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  font-size: 9px;
  font-weight: 700;
  color: #fff;
  background: #4e54c8;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Filter popover (opens near icon) */
.filter-popover {
  position: fixed;
  z-index: 9999;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid #e0e0e0;
  width: 320px;
  max-height: 400px;
  display: flex;
  flex-direction: column;
}
.filter-popover-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #eee;
}
.filter-popover-title {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
}
.filter-popover-close {
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: none;
  font-size: 18px;
  color: #888;
  cursor: pointer;
  line-height: 1;
}
.filter-popover-close:hover {
  color: #333;
}
.filter-popover-body {
  padding: 10px 12px;
  overflow-y: auto;
  flex: 1;
}
.filter-popover-search {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 13px;
  margin-bottom: 8px;
}
.filter-popover-search:focus {
  outline: none;
  border-color: #4e54c8;
}
.filter-popover-list {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 6px;
}
.filter-popover-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
  margin: 0;
  font-size: 12px;
}
.filter-popover-item:last-child {
  border-bottom: none;
}
.filter-popover-item:hover {
  background: #f8f9ff;
}
.filter-popover-item input[type="checkbox"] {
  width: 14px;
  height: 14px;
  cursor: pointer;
}
.filter-popover-empty {
  padding: 16px;
  text-align: center;
  color: #888;
  font-size: 12px;
}
.duration-filter-mode {
  margin-bottom: 10px;
}
.duration-filter-inputs {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.duration-filter-inputs .filter-popover-search {
  margin-bottom: 0;
}
.filter-popover-footer {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  padding: 10px 12px;
  border-top: 1px solid #eee;
}

.loading-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.32);
  backdrop-filter: blur(2px);
  z-index: 10000;
}

.loading-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 18px 22px;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
}

.loading-spinner {
  width: 52px;
  height: 52px;
  border: 4px solid rgba(78, 84, 200, 0.18);
  border-top-color: #4e54c8;
  border-radius: 50%;
  animation: dnq-spin 0.8s linear infinite;
}

.loading-text {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

@keyframes dnq-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>